/**
 * premium.ts — Modelos que estreiam os recursos avançados do Studio
 *
 * Estes modelos existem para mostrar, na prática, o que o motor passou a saber
 * fazer nas últimas fases: máscaras de recorte, inclinação em perspectiva,
 * formas decorativas, efeitos visuais versionados e movimento com orçamento.
 * Todos usam blocos do catálogo único — nada de bloco novo — e todos foram
 * conferidos em tela de celular.
 *
 * RELEASE: v1.0.0 - 2026-09-11 - Seis modelos novos (revisão de modelos,
 *          miniaturas e documentação)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { FOTO, WEB, b } from './_shared';

const modelos: StudioPatternDefinition[] = [
  // ------------------------------------------------------------------ Capa
  {
    key: 'capa.vitrine-arco',
    category: 'capa',
    label: 'Vitrine em arco',
    description: 'Foto recortada em arco, título editorial e prova curta.',
    preview: 'hero-split',
    outputs: WEB,
    order: 21,
    build: () => [
      b(
        'section.layered',
        {
          layout: 'split',
          paddingY: 'xl',
          align: 'left',
          maxWidth: '1120px',
          backgroundColor: '#faf7f2',
          motion: 'fade-up',
        },
        [
          b('ui.badge', { text: 'Coleção 2026' }),
          b('ui.title', { text: 'Feito à mão, pensado para durar', size: 'xl', level: 'h1' }),
          b('ui.paragraph', {
            text: 'Peças produzidas em pequenas séries, com acabamento conferido uma a uma.',
            size: 'lg',
          }),
          b('ui.buttons', { items: 'Ver a coleção|#galeria|solid\nFalar com o atelier|#contato|outline', size: 'lg' }),
          b('ui.image', {
            src: FOTO.interior,
            alt: 'Ambiente do atelier',
            ratio: 'portrait',
            mask: 'arch',
            shadow: 'lg',
          }),
        ],
      ),
    ],
  },

  {
    key: 'capa.tela-inclinada',
    category: 'capa',
    label: 'Produto em perspectiva',
    description: 'Tela do produto inclinada sobre fundo escuro com brilho.',
    preview: 'hero-split',
    outputs: WEB,
    order: 22,
    build: () => [
      b(
        'section.layered',
        {
          layout: 'split',
          paddingY: 'xl',
          align: 'left',
          maxWidth: '1160px',
          backgroundGradient: 'mesh-dark',
          glow: 'right',
          textColor: '#ffffff',
          motion: 'fade-up',
        },
        [
          b('ui.badge', { text: 'Novo' }),
          b('ui.title', { text: 'Tudo o que a sua operação precisa, em uma tela', size: 'xl', level: 'h1' }),
          b('ui.paragraph', {
            text: 'Metas, atendimento e cobrança no mesmo painel, atualizados em tempo real.',
            size: 'lg',
          }),
          b('ui.buttons', { items: 'Testar agora|#formulario|solid\nVer demonstração|#video|ghost', size: 'lg' }),
          b('ui.image', {
            src: FOTO.painel,
            alt: 'Painel do produto',
            ratio: 'landscape',
            radius: 'xl',
            tilt: 'left',
            effect: 'glass-dark',
          }),
        ],
      ),
    ],
  },

  // --------------------------------------------------------- Texto e imagem
  {
    key: 'texto-imagem.recorte-organico',
    category: 'texto-imagem',
    label: 'Recorte orgânico',
    description: 'Foto com recorte orgânico ao lado de um texto calmo.',
    preview: 'split-left',
    outputs: WEB,
    order: 21,
    build: () => [
      b('section.layered', { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1080px', motion: 'fade' }, [
        b('ui.image', {
          src: FOTO.consultoria,
          alt: 'Equipe em reunião',
          ratio: 'square',
          mask: 'organic',
          shadow: 'md',
        }),
        b('ui.title', { text: 'Perto de quem decide', size: 'lg', level: 'h2' }),
        b('ui.paragraph', {
          text: 'Um time dedicado acompanha a implantação do começo ao fim, com reuniões semanais e metas claras.',
        }),
        b('ui.buttons', { items: 'Conhecer o método|#etapas|outline' }),
      ]),
    ],
  },

  // ------------------------------------------------------------- Recursos
  {
    key: 'recursos.editorial-formas',
    category: 'recursos',
    label: 'Diferenciais com formas',
    description: 'Cartões de vidro sobre uma malha decorativa discreta.',
    preview: 'grid-3',
    outputs: WEB,
    order: 21,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'lg', align: 'center', maxWidth: '1120px', pattern: 'grid', motion: 'fade-up' },
        [
          b('ui.shape', { shape: 'orb', size: 'lg', color: 'rgba(56,189,248,.35)' }),
          b('ui.title', { text: 'Por que trocam para a gente', size: 'lg', level: 'h2', align: 'center' }),
          b('page.features', {
            columns: 3,
            items: [
              'Implantação em 15 dias|Sem parar a operação atual.',
              'Suporte que responde|Atendimento humano em minutos.',
              'Preço previsível|Sem cobrança surpresa no fim do mês.',
            ].join('\n'),
          }),
        ],
      ),
    ],
  },

  // ---------------------------------------------------------------- Preços
  {
    key: 'precos.vidro-destaque',
    category: 'precos',
    label: 'Planos em vidro',
    description: 'Planos sobre fundo escuro, com o plano do meio destacado.',
    preview: 'pricing',
    outputs: WEB,
    order: 21,
    build: () => [
      b(
        'section.layered',
        {
          paddingY: 'xl',
          align: 'center',
          maxWidth: '1080px',
          backgroundGradient: 'mesh-dark',
          textColor: '#ffffff',
          motion: 'fade',
        },
        [
          b('ui.title', { text: 'Planos simples, sem letra miúda', size: 'lg', level: 'h2', align: 'center' }),
          b('page.pricing', {
            columns: 3,
            items: [
              'Início|R$ 149|por mês|1 usuário;Suporte por e-mail|Assinar|#',
              'Time|R$ 349|por mês|5 usuários;Suporte prioritário;Automação|Assinar|#',
              'Escala|R$ 749|por mês|Usuários ilimitados;Gerente de conta|Falar com vendas|#',
            ].join('\n'),
            highlight: 2,
          }),
        ],
      ),
    ],
  },

  // ------------------------------------------------------- Chamada para ação
  {
    key: 'cta.faixa-de-luz',
    category: 'cta',
    label: 'Faixa de luz',
    description: 'Convite curto sobre um feixe de luz em movimento suave.',
    preview: 'cta',
    outputs: WEB,
    order: 21,
    build: () => [
      b(
        'section.layered',
        {
          paddingY: 'lg',
          align: 'center',
          maxWidth: '900px',
          backgroundColor: '#0b1120',
          textColor: '#ffffff',
          radius: 'xl',
          motion: 'fade-up',
        },
        [
          b('ui.shape', { shape: 'beam', size: 'lg', color: 'rgba(125,211,252,.35)' }),
          b('ui.title', { text: 'Comece hoje, sem risco', size: 'lg', level: 'h2', align: 'center' }),
          b('ui.paragraph', { text: 'Primeiros 14 dias por nossa conta.', align: 'center', maxWidth: '520px' }),
          b('ui.buttons', { items: 'Criar minha conta|#formulario|solid', align: 'center', size: 'lg' }),
        ],
      ),
    ],
  },
];

registerPatterns(modelos);
